import {
  GalaxyFilterChipDependencies,
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInputOverride,
  GalaxyFilterInterface,
} from '@vendasta/galaxy/filter/chips';
import { FieldType, FieldValueInterface as SdkFieldValueInterface } from '@vendasta/crm';
import { Observable, of } from 'rxjs';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { CustomCellComponent, GalaxyColumnDef, Row } from '@vendasta/galaxy/table';
import { inject, InjectionToken, Type, ViewContainerRef } from '@angular/core';
import { Properties } from 'posthog-js';
import { CardDataType } from '@vendasta/galaxy/contact-info-card';
import { CrmFieldOptionsService } from './shared-services/crm-services/crm-field-options.service';
import { CrmFiltersService } from './shared-services/crm-services/crm-filters.service';
import { CRMTrackingService } from './shared-services/page-analytics.service';
import { FieldValidation } from './shared-components/model-driven-form/field-validation';
import { User } from '@vendasta/iamv2';
export { FieldType } from '@vendasta/crm';

export interface ProfileCard {
  component: any;
}

export interface CRMSelectAllOptions {
  filters?: GalaxyFilterInterface[];
  search?: string;
  useSelectAll?: boolean;
  totalObjects?: number;
}

export interface UserWithDisplayName extends User {
  displayName: string;
}

export type MultiRowActionFunc = (rows: Row[]) => void;
export type SelectAllActionFunc = (rows: Row[], selectOptions?: CRMSelectAllOptions) => void;
export type SingleRowActionFunc = (row: Row) => void;
export type ObjectType = 'Contact' | 'Company' | 'Activity';
export const ObjectTypes: ObjectType[] = ['Contact', 'Company', 'Activity'];
// Extra object types that can be associated with an activity
export type ObjectAssociationType = 'Opportunity' | 'User';
export type RowVisibilityFunc = (row: Row, type: ObjectType) => boolean;

export interface MultiRowAction {
  label: string;
  callback: MultiRowActionFunc;
}
export interface SelectAllAction {
  label: string;
  callback: SelectAllActionFunc;
  selectAllVisible?: boolean;
}

export interface SingleRowAction {
  label: string;
  callback: SingleRowActionFunc;
  visible?: RowVisibilityFunc;
}

export interface CRMRowObject {
  objectId: string;
  objectType: ObjectType;
}

export interface TableCustomCell {
  fieldIds: string[];
  customCellComponent: Type<CustomCellComponent>;
  columnDefinition: GalaxyColumnDef;
}

export interface CRMFilterOverride extends GalaxyFilterInputOverride {
  title: string;
}

export interface ActionButton {
  component: any;
}

export type RequiresField<T, K extends keyof T> = T & { [P in K]-?: T[P] };
export type FieldValueInterface = RequiresField<SdkFieldValueInterface, 'fieldId'>;

export abstract class FormInputComponent {
  readOnly = false;
  inlineEdit$: Observable<boolean> = of(false);
  objectType: ObjectType = 'Contact';

  abstract set control(control: UntypedFormControl);
  abstract get control(): UntypedFormControl;

  abstract set fieldId(fieldId: string);

  abstract set label(label: string);

  abstract get hasModification(): boolean;

  abstract get value(): FieldValueInterface;
}

export interface FormCustomInput {
  fieldId: string;
  component: Type<FormInputComponent>;
}

export interface FormDefaultInputValue {
  fieldId: string;
  value: any;
}

export interface SimplifiedUser {
  userId: string;
  firstName: string;
  lastName: string;
  displayName: string;
}

export interface UserService {
  searchUsers?(searchTerm?: string): Observable<SimplifiedUser[]>;

  getMultiUsers(userIds: string[]): Observable<SimplifiedUser[]>;
}

export interface AccessService {
  canViewAllCampaigns$?: Observable<boolean>;
  canModifyAutomations$?: Observable<boolean>;
  hasPartnerPermissions$?: Observable<boolean>;
  canAccessSales$?: Observable<boolean>;
  canAccessOrders$?: Observable<boolean>;
  canAccessAutomations$?: Observable<boolean>;
}

export const CRM_ACCESS_SERVICE_TOKEN = new InjectionToken<AccessService>('Service for the crm for user access checks');

export interface SimplifiedOpportunity {
  opportunityId: string;
  name: string;
}

export interface OpportunityService {
  searchOpportunities(searchTerm: string): Observable<SimplifiedOpportunity[]>;

  getMultiOpportunity(opportunityIds: string[]): Observable<SimplifiedOpportunity[]>;

  setAccountGroupId(accountGroupId: string): void;
}

// Dependencies related to specific object types
interface CrmObjectDependencies {
  multiRowTableActions$?: Observable<MultiRowAction[]>;
  singleRowTableActions$?: Observable<SingleRowAction[]>;
  selectAllTableActions$?: Observable<SelectAllAction[]>;
  profileCards$?: Observable<ProfileCard[]>;
  actionButtons$?: Observable<ActionButton[]>;
  baseColumnIds?: string[];
  filterInputOverrides$?: Observable<CRMFilterOverride[]>;
  tableCustomCells?: TableCustomCell[];
  formCustomInputs?: FormCustomInput[];
  formDefaultOnCreateInputValues$?: Observable<FormDefaultInputValue[]>;
  showRightHandProfilePanel$?: Observable<boolean>; // default to show right hand profile panel
}

export interface CrmDependencies {
  appID?: string;
  // the prefix for the route, e.g. /crm. necessary to avoid using relative routing with CRM lib subrouter
  routePrefix$: Observable<string>;

  // A namespace is an instance of the CRM
  // e.g. VUNI for a partner managing their contacts in Partner Center,
  // or AG-12345 for an SMB managing their contacts in Business Center.
  // (This distinction gets weirder in the future when we start merging these Centers,
  // but just roll with it for now.)
  namespace$: Observable<string>;

  // When the CRM is used in Business Center, the namespace is the account group id.
  // This will give you the parent namespace, which is the partner id.
  parentNamespace$: Observable<string>;

  // Actions that can be taken either on a single row or a selection of rows from the CRM table.
  // Actions are defined by the app instantiating the CRM and will be different from app to app.
  // eg. RM could define an action to send a review request to a set of selected contacts. RM supplies all the
  // config for the action and the CRM table invokes the provided function when it is clicked.
  multiRowTableActions$?: Observable<MultiRowAction[]>;
  singleRowTableActions$?: Observable<SingleRowAction[]>;
  selectAllActions$?: Observable<SelectAllAction[]>;

  // featureFlags$
  viewProfileFlag$: Observable<boolean>;
  viewProspectorFlag$?: Observable<boolean>;

  // the unified user id of the current user
  currentUserId$: Observable<string>;

  // dependencies by object type
  company?: CrmObjectDependencies;
  contact?: CrmObjectDependencies;
  task?: CrmObjectDependencies;

  showBackToAccountsButton?: boolean;

  // dependencies for namespace specific shared-services
  services?: {
    userService?: UserService;
    opportunityService?: OpportunityService;
    accessService?: AccessService;
  };

  openAccountGroupConversation?: (accountGroupId: string) => void | Promise<void>;
  openContactConversation?: (contactId: string) => void | Promise<void>;

  // Helps determine whether a partner has access to a feature
  hasAccessToFeature$?: (feature: string) => Observable<boolean>;
  openRestrictedDialog?: (featureId: string) => void;

  // Client specific actions to shared components
  customActions?: {
    // navigates to the fulfillment projects page for a given company
    navigateToFulfillmentProjects?: (row: CRMRowObject) => void;
    openOpportunitiesDetails?: (opportunityId: string, accountGroupId: string) => void;
  };
}

export const CrmInjectionToken = new InjectionToken<CrmDependencies>('dependencies for the crm');

export interface CompanyProfileCardData {
  company_id: string;
  account_group_id?: string;
  name?: string;
  group_id?: string;
}

export const COMPANY_PROFILE_CARD_DATA_TOKEN = new InjectionToken<CompanyProfileCardData>('company-profile-card-data');

export interface PageAnalyticsDependencies {
  trackEvent(action: string, properties?: Properties): void;
}

export const PageAnalyticsInjectionToken = new InjectionToken<PageAnalyticsDependencies>(
  'dependencies for page analytics',
);
export type ActivityType = 'Note' | 'Email' | 'Call' | 'Meeting' | 'Task' | 'Communication';
export type CommunicationSubType = 'SMS' | 'LinkedIn' | 'Inbox';
export type RecordChangeType = 'CREATED' | 'IMPORTANT_FIELD_UPDATED';

export interface CardData extends CardDataType {
  tags?: string[];
  hideAvatar?: boolean;
  accountGroupCompanyName?: string;
  accountGroupCompanyId?: string;
  associationId?: string;
}

export function tableFiltersInjectionTokenGenerator(
  objectType: ObjectType,
  activityType?: ActivityType,
): () => GalaxyFilterChipDependencies {
  if (!objectType) {
    console.error('objectType is required for tableFiltersInjectionTokenGenerator');
    return () => ({}) as GalaxyFilterChipDependencies;
  }

  return () => {
    const crmFieldOptionsService = inject(CrmFieldOptionsService);
    const crmFiltersService = inject(CrmFiltersService);
    const crmConfigDependencies = inject(CrmInjectionToken);
    const deps: GalaxyFilterChipDependencies = {
      listObjectFilters(searchTerm: string): Observable<GalaxyFilterDefinitionInterface[]> {
        return crmFiltersService.listObjectFilters$(searchTerm, objectType, activityType);
      },
      getFieldOptions(fieldId: string, search: string): Observable<string[]> {
        return crmFieldOptionsService.getFieldOptions(fieldId, search, objectType);
      },
      getFilterInputOverrides(): Observable<GalaxyFilterInputOverride[]> {
        switch (objectType) {
          case 'Contact':
            return crmConfigDependencies.contact?.filterInputOverrides$ ?? of([] as GalaxyFilterInputOverride[]);
          case 'Company':
            return crmConfigDependencies.company?.filterInputOverrides$ ?? of([] as GalaxyFilterInputOverride[]);
          case 'Activity':
            switch (activityType) {
              case 'Task':
                return crmConfigDependencies.task?.filterInputOverrides$ ?? of([] as GalaxyFilterInputOverride[]);
              default:
                return of([] as GalaxyFilterInputOverride[]);
            }
          default:
            return of([] as GalaxyFilterInputOverride[]);
        }
      },
      getInitialAppliedFilters(): Promise<GalaxyFilterInterface[]> {
        return crmFiltersService.getInitialAppliedFilters$(objectType, activityType, true);
      },
    };
    return deps;
  };
}

export function pageAnalyticsInjectionTokenGenerator(objectType: ObjectType): () => PageAnalyticsDependencies {
  return () => {
    const crmTrackingService = inject(CRMTrackingService);
    const deps: PageAnalyticsDependencies = {
      trackEvent(action: string, properties?: Properties): void {
        crmTrackingService.trackEvent(objectType, action, properties);
      },
    };
    return deps;
  };
}

export interface DropdownOptionInterface {
  label?: string;
  value?: string;
}

export interface FieldSchemaInterface {
  fieldId: string;
  fieldType: FieldType;
  fieldName: string;
  fieldDescription: string;
  dropdownOptions: DropdownOptionInterface[];
  currencyCode: string;
  value: any;
  readOnly: boolean;
  hasDefaultValue?: boolean;
}

export interface FieldInterface {
  fieldId: string;
  name: string;
  value: any;
  description: string;

  // Gets the field value for the backend
  fieldValue(): FieldValueInterface;

  // Checks if the field received a update
  hasModification(): boolean;
}

export interface FieldGroupInterface {
  description?: string;
  fieldIds: string[];
}

export const ActivityTaskStatus = {
  Open: 'Open',
  Completed: 'Completed',
};

export interface FieldOptionsDependencies {
  getFieldOptions(fieldId: string, search: string): Observable<string[]>;
}

export const FieldOptionsInjectionToken = new InjectionToken<FieldOptionsDependencies>(
  'dependencies for getting field options in the model-driven form',
);

export enum LoadingStatus {
  LOADING = 1,
  OK = 2,
  EMPTY = 3,
  ERROR = 4,
}

export enum SavingStatus {
  NOT_SAVING = 1,
  SAVING = 2,
  OK = 3,
  ERROR = 4,
}

export enum NullValue {
  NULL_VALUE = 0,
}

export interface RenderableFieldInterface extends FieldInterface {
  formControl: AbstractControl;
  validation: FieldValidation;
  objectType: ObjectType;
  readOnly: boolean;

  // Chooses the correct component to display information
  render(viewContainerRef: ViewContainerRef): void;

  setValidation(validation: FieldValidation): void;
}

export interface RenderableFieldGroupInterface {
  description?: string;
  fields: RenderableFieldInterface[];
  editableFieldCount: number;
}

export interface FieldConfigInterface {
  fieldSchemas?: FieldSchemaInterface[];
  fieldGroups?: FieldGroupInterface[];
}

export function modelDrivenFormFieldOptionsInjectionTokenGenerator(
  objectType: ObjectType,
): () => FieldOptionsDependencies {
  return () => {
    const crmFieldOptionsService = inject(CrmFieldOptionsService);
    const deps: FieldOptionsDependencies = {
      getFieldOptions(fieldId: string, search: string): Observable<string[]> {
        return crmFieldOptionsService.getFieldOptions(fieldId, search, objectType);
      },
    };
    return deps;
  };
}

export interface ContactProfileCardData {
  id?: string;
  name?: string;
}

export const CONTACT_PROFILE_CARD_DATA_TOKEN = new InjectionToken<ContactProfileCardData>('contact-profile-card-data');

export interface ActionButtonData {
  account_group_id?: string;
}

export const PROFILE_ACTION_BUTTON_DATA_TOKEN = new InjectionToken<ActionButtonData>('action-button');

export const PrimaryAccountGroupIDForCrmObject = new InjectionToken<Observable<string>>(
  'primary account group ID for crm object in a given context',
);
